import {
  fetchDocumentsCollections,
  fetchUploadFileURL,
  fileNumberExceeded,
  newNotification,
  trackDocCollectorFileUpload,
  uploadFile,
} from 'actions';
import { FileInput } from 'components/ui/FileInput/FileInput';
import { UPLOAD_DOCUMENT_SUCCESS } from 'constants/index';
import type { File as FileModel } from 'models';
import type { ChangeEvent } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import { useTranslations } from 'utils';

type PropsFromRedux = ConnectedProps<typeof connector>;
type DocumentUploaderProps = PropsFromRedux & {
  documentUploadURL: string;
  files: FileModel[];
  maxFileSize: number;
};

const MAX_FILES_PER_DOCUMENT = 20;
const SUPPORTED_MIME_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'application/pdf',
];
const FILE_INPUT_ACCEPT = SUPPORTED_MIME_TYPES.join(',');
const BYTES_PER_MEGABYTE = 1024 * 1024;

const toMbString = (bytes: number) =>
  Math.round(bytes / BYTES_PER_MEGABYTE).toString();

export const DocumentUploaderComponent = ({
  documentUploadURL,
  fetchDocumentsCollections,
  fetchUploadFileURL,
  fileNumberExceeded,
  files,
  maxFileSize,
  newNotification,
  trackDocCollectorFileUpload,
  uploadFile,
}: DocumentUploaderProps) => {
  const i18n = useTranslations();

  const handleUploadFile = async (file: File, uploadFileURL: string) => {
    const result = await uploadFile(file, uploadFileURL);

    return result?.type === UPLOAD_DOCUMENT_SUCCESS;
  };

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const uploadedFiles = target.files;

    if (!uploadedFiles) {
      return;
    }
    const totalFiles = files.length + uploadedFiles.length;

    if (totalFiles > MAX_FILES_PER_DOCUMENT) {
      return fileNumberExceeded();
    }
    if (!validateFiles(uploadedFiles)) {
      const fileNames = Array.from(uploadedFiles)
        .map(({ name }) => name)
        .join(', ');

      newNotification({
        message: i18n.t('notifications.upload_document_failure', {
          name: fileNames,
        }) as string,
        type: 'error',
      });
      return;
    }

    try {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const uploadFileURL = await fetchUploadFileURL(documentUploadURL);
        const uploaded = await handleUploadFile(file, uploadFileURL);

        const response = uploaded ? 'success' : 'failure';
        trackDocCollectorFileUpload(response);

        await fetchDocumentsCollections();
      }
    } catch (error) {
      newNotification({
        message: (error as Error).message,
        type: 'error',
      });
    } finally {
      target.value = '';
    }
  };

  return (
    <FileInput
      accept={FILE_INPUT_ACCEPT}
      buttonText={i18n.t('documentCollector.submit') as string}
      className="DocumentCollector-documentUploader-input"
      hint={
        i18n.t('documentCollector.hint', {
          maxFileSize: toMbString(maxFileSize),
        }) as string
      }
      onChange={handleChange}
      multiple
    />
  );
};

const mapDispatchToProps = {
  fetchDocumentsCollections,
  fetchUploadFileURL,
  fileNumberExceeded,
  newNotification,
  trackDocCollectorFileUpload,
  uploadFile,
};

const connector = connect(null, mapDispatchToProps);

export const DocumentUploader = connector(DocumentUploaderComponent);

const validateFiles = (files: FileList) =>
  Array.from(files).every(file => SUPPORTED_MIME_TYPES.includes(file.type));
